/**
 * Adapter: DrillSpec ↔ TacticsLayout
 *
 * Drills are persisted in `drills.drill_spec` as the normalised-pitch
 * `DrillSpec` shape. The canvas the coach edits in is Josh's tactics
 * board (TacticsBoard2D / 3D), which works on `TacticsLayout` — world
 * metres, pitch-centred, with a different player/arrow shape.
 *
 * This module bridges the two with a lossy converter:
 *   - Player entities  (drill)  ↔ TacticsPlayerSpot (tactics)
 *   - Movement arrows  (drill)  ↔ TacticsArrow      (tactics)
 *   - Cones / mannequins / goals → DROPPED on tactics → drill round-trip
 *     (tactics has no equipment surface; v1 limitation)
 *   - Zones, texts, keyframes — passed through where possible; ignored
 *     otherwise so the canvas still renders the core entities + arrows.
 *
 * Coordinate convention:
 *   DrillSpec: x ∈ [0, 1] (left → right), y ∈ [0, 1] (far → near).
 *   TacticsLayout: x ∈ [−34, +34] (left → right), z ∈ [−52.5, +52.5]
 *     (far → near, but z=+ is the attacking end so we flip the sign).
 */

import type { DrillSpec, DrillEntity, DrillArrow, NormPoint } from '@4cm/core/drills/types';
import { visibleRegion, type PitchView, type CustomPitch } from '../components/drillCanvas/pitchArea';
import type { PitchSpec } from '../components/drillCanvas/pitchSpecs';

// Local re-declaration of the TacticsLayout subset we touch — keeps the
// adapter loosely coupled to Josh's schema. The full type is at
// `@4cm/core/tactics/types`; we only use the v=1 fields plus arrows.
export interface TacticsSpot { role: string; x: number; y: number; z: number }
export interface TacticsArrow {
  id: string;
  points: { x: number; z: number }[];
  color: string;
  playerKey?: string;
}

export interface TacticsLayoutLike {
  homeFormation: string;
  awayFormation: string;
  homeSpots: TacticsSpot[];
  awaySpots: TacticsSpot[];
  arrows: TacticsArrow[];
}

const PITCH_WIDTH_M = 68;   // sideline-to-sideline (x)
const PITCH_LENGTH_M = 105; // goal-to-goal (z)
const DEFAULT_FORMATION = '4-3-3';

/** Normalised [0,1] → world metres pitch-centred. */
function normToMetres(p: NormPoint): { x: number; z: number } {
  const [nx, ny] = p;
  const x = (nx - 0.5) * PITCH_WIDTH_M;
  // Drill y=0 is the far end (defending goal). Tactics z=−52.5 is the
  // far end too. Drill y=1 is the attacking end. Same orientation.
  const z = (ny - 0.5) * PITCH_LENGTH_M;
  return { x, z };
}

/** World metres pitch-centred → normalised [0,1]. */
function metresToNorm(x: number, z: number): NormPoint {
  const nx = clamp01(x / PITCH_WIDTH_M + 0.5);
  const ny = clamp01(z / PITCH_LENGTH_M + 0.5);
  return [nx, ny];
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

// ----------------------------------------------------------------------------
// View-aware adapter for 3D drill rendering
// ----------------------------------------------------------------------------
// The original normToMetres / metresToNorm assume the drill spans the FULL
// pitch. For half / quarter / custom pitch views, drill [0..1] coords are
// over the visible region — these helpers consult `visibleRegion()` to
// scale + offset into Josh's 3D world (metres, pitch-centred).
//
// Josh's coord system (matches normToMetres above):
//   x = sideline-to-sideline   range ±34m for 11v11 senior
//   z = goal-to-goal           range ±52.5m, drill y=0 ↔ z=-52.5 (defending)

/** Drill-normalised [0..1] over the visible region → world metres (x, z).
 *  When view='full' this is identical to the legacy normToMetres. */
export function drillNormToMetres3D(
  p: NormPoint,
  spec: PitchSpec,
  view: PitchView,
  custom?: CustomPitch | null,
): { x: number; z: number } {
  const r = visibleRegion(spec, view, custom);
  const x = r.cy + (p[0] - 0.5) * r.width;
  const z = r.cx + (p[1] - 0.5) * r.length;
  return { x, z };
}

/** World metres (x, z) → drill-normalised [0..1] over the visible region.
 *  Inverse of drillNormToMetres3D. Clamps to [0, 1] so out-of-region
 *  values still round-trip to a usable position. */
export function metresToDrillNorm3D(
  x: number,
  z: number,
  spec: PitchSpec,
  view: PitchView,
  custom?: CustomPitch | null,
): NormPoint {
  const r = visibleRegion(spec, view, custom);
  const nx = clamp01((x - r.cy) / r.width + 0.5);
  const ny = clamp01((z - r.cx) / r.length + 0.5);
  return [nx, ny];
}

// ----------------------------------------------------------------------------
// DrillSpec → TacticsLayout
// ----------------------------------------------------------------------------

export function drillSpecToTacticsLayout(spec: DrillSpec): TacticsLayoutLike {
  const homeSpots: TacticsSpot[] = [];
  const awaySpots: TacticsSpot[] = [];

  spec.entities.forEach((e: DrillEntity, idx: number) => {
    if (e.type === 'ball' || e.type === 'official') return;
    const m = normToMetres(e.position);
    const spot: TacticsSpot = {
      role: e.role ?? e.label ?? String(idx + 1),
      x: m.x,
      y: 0.6,
      z: m.z,
    };
    if (e.type === 'home') homeSpots.push(spot);
    else if (e.type === 'away') awaySpots.push(spot);
    else homeSpots.push(spot);
  });

  const arrows: TacticsArrow[] = spec.arrows.map((a: DrillArrow) => {
    const from = arrowEndpointMetres(a.from, spec.entities);
    const to = arrowEndpointMetres(a.to, spec.entities);
    return {
      id: a.id,
      points: from && to ? [from, to] : [{ x: 0, z: 0 }, { x: 0, z: 0 }],
      color: a.color ?? '#F39C12',
    };
  }).filter((a) => a.points[0].x !== 0 || a.points[1].x !== 0 || a.points[0].z !== 0 || a.points[1].z !== 0);

  return {
    homeFormation: DEFAULT_FORMATION,
    awayFormation: DEFAULT_FORMATION,
    homeSpots,
    awaySpots,
    arrows,
  };
}

function arrowEndpointMetres(
  endpoint: DrillArrow['from'],
  entities: DrillEntity[],
): { x: number; z: number } | null {
  if (endpoint.kind === 'point') return normToMetres(endpoint.position);
  const entity = entities.find((e) => e.id === endpoint.entityId);
  if (!entity) return null;
  return normToMetres(entity.position);
}

// ----------------------------------------------------------------------------
// TacticsLayout → DrillSpec (best-effort; cones / texts / zones / keyframes
// preserved from the prior DrillSpec if provided as `base`).
// ----------------------------------------------------------------------------

export function tacticsLayoutToDrillSpec(
  layout: TacticsLayoutLike,
  base?: DrillSpec | null,
): DrillSpec {
  const entities: DrillEntity[] = [];

  layout.homeSpots.forEach((s, idx) => {
    entities.push({
      id: `h-${idx}`,
      type: 'home',
      position: metresToNorm(s.x, s.z),
      label: s.role,
      role: s.role,
    });
  });
  layout.awaySpots.forEach((s, idx) => {
    entities.push({
      id: `a-${idx}`,
      type: 'away',
      position: metresToNorm(s.x, s.z),
      label: s.role,
      role: s.role,
    });
  });

  const arrows: DrillArrow[] = layout.arrows.map((a) => {
    const start = a.points[0] ?? { x: 0, z: 0 };
    const end = a.points[a.points.length - 1] ?? start;
    return {
      id: a.id,
      from: { kind: 'point', position: metresToNorm(start.x, start.z) },
      to: { kind: 'point', position: metresToNorm(end.x, end.z) },
      color: a.color,
      kind: 'run',
    };
  });

  // Preserve cones / zones / texts / keyframes from the prior DrillSpec
  // because TacticsLayout has nowhere to store them. They survive the
  // round-trip but won't be visible/editable while the coach is in the
  // tactics canvas — that's the v1 limitation.
  //
  // The drill-canvas (rolePlayers / pitchViewExtended / targetPlayerCount
  // / etc.) attaches custom JSONB extras to base — spreading base FIRST
  // lets them through, then the canonical fields below override.
  return {
    ...(base ?? {}),
    schemaVersion: 1,
    pitchPresetId: base?.pitchPresetId ?? 'full_11v11',
    customPitch: base?.customPitch ?? null,
    pitchView: base?.pitchView,
    entities,
    cones: base?.cones ?? [],
    // Prefer base.arrows when present (drill canvas owns its arrow
    // store), otherwise fall back to TacticsLayout-derived arrows.
    arrows: (base?.arrows && base.arrows.length > 0) ? base.arrows : arrows,
    zones: base?.zones ?? [],
    texts: base?.texts ?? [],
    keyframes: base?.keyframes ?? [],
    showGrid: base?.showGrid,
  };
}
